import { Injectable, inject } from '@angular/core';
import { getMutationResult, GraphApiService } from '@nuso/common/data-access-api';
import {
  deletePlatformServiceMutation,
  deprovisionPlatformServiceMutation,
  getPlatformServiceQuery,
  platformServiceReportDetailsQuery,
  platformServiceReportsQuery,
  provisionPlatformServiceMutation,
  provisionPlatformServiceAfterAuthorizationMutation,
  serviceTypesQuery,
  setPlatformServiceEnabledStatusMutation,
  updatePlatformServicNameMutation,
  customerAvailableServiceTypesAndAssociatedServicesQuery,
} from './platform-services-queries';
import { PlatformService, ServiceType, TnProvisionFormSettings } from '@nuso/services/types';
import { PlatformServiceEmbeddableReport, PlatformServiceReportSummary } from '@nuso/reporting/types';
import { catchError, filter, first, map, take, tap } from 'rxjs';
import { NotificationService } from '@nuso/common/util-errors';
import { omitNulls, renameFields } from '@nuso/common/util-object';

function omitNullsOfTnProvisionFormSettings(settings: TnProvisionFormSettings | null | undefined) {
  if (!settings) return settings;
  let settingsWithNullRemoved: any = omitNulls(settings);
  if (settingsWithNullRemoved.subscription) {
    settingsWithNullRemoved = { ...settingsWithNullRemoved, subscription: omitNulls(settingsWithNullRemoved.subscription) };
  }

  return settingsWithNullRemoved;
}

export function normalizeServiceTypes(serviceTypes: ServiceType[] | null): ServiceType[] {
  return (serviceTypes || []).map((serviceType) => {
    // We are treating the null values in the tnProvisionFormSettings as if they were undefined
    // We can't get undefined from the GraphQL API, and the typePolicies in the cache are not working
    // as expected, so we have to do this manually
    const defaultSettings = serviceType.defaultSettings || {};
    const tnProvisionFormSettings = defaultSettings.tnProvisionFormSettings || {};
    return {
      ...serviceType,
      defaultSettings: {
        ...defaultSettings,
        tnProvisionFormSettings: omitNullsOfTnProvisionFormSettings(tnProvisionFormSettings)
      },
    }
  })
    .sort((a, b) => a.position - b.position);
}

function normalizeServiceSettingsFields(serviceSettings: any | null) {
  if (!serviceSettings) {
    return null;
  }

  return renameFields(serviceSettings, {
    'tenantIdString': 'tenantId',
    'trunkIdString': 'trunkId',
  });
}


export function normalizePlatformServices(services: PlatformService[]): PlatformService[] {
  return services.map(s => {
    const { tnProvisionFormSettings, serviceSettings } = s.settings || {};
    return {
      ...s,
      settings: {
        ...s.settings,
        serviceSettings: normalizeServiceSettingsFields(serviceSettings),
        tnProvisionFormSettings: omitNullsOfTnProvisionFormSettings(tnProvisionFormSettings)
      }
    }
  });
}


@Injectable({
  providedIn: 'root',
})
export class PlatformServiceService {
  private graphApi = inject(GraphApiService);
  private notificationService = inject(NotificationService);


  // This is called from the resolver, so we need the observable
  // to complete.
  // We use the rethrowErrors option here to make it easier for
  // use to determine if an error happened vs just getting null back
  // We use undefined to signal that we got an error, as the graph api
  // returns null when no service is found
  fetchPlatformService(id: string) {
    return this.graphApi.watchQuery<PlatformService[] | null>({
      query: getPlatformServiceQuery,
      variables: { id },
      fetchPolicy: 'network-only',
      rethrowErrors: true
    }).pipe(
      take(1),
      this.notificationService.catchGraphError(`Failed to fetch platform service with id ${id}`, undefined),
      tap((service) => {
        if (service === null) {
          this.notificationService.addError(`Failed to find platform service with id: ${id}`)
        }
      }),
      map((service) => service ? service : null),
    )
  }

  watchPlatformService(serviceId: string) {
    return this.graphApi.watchQuery<PlatformService | null>({
      query: getPlatformServiceQuery,
      variables: { id: serviceId },
      fetchPolicy: 'cache-and-network',
    });
  }

  getServiceTypesQueryRef() {
    return this.graphApi.watchQueryRef<ServiceType[]>({
      query: serviceTypesQuery
    })
  }

  getServiceTypes() {
    return this.graphApi.watchQuery<ServiceType[]>({
      query: serviceTypesQuery,
    }).pipe(
      map((serviceTypes) => normalizeServiceTypes(serviceTypes)),
    );
  }

  getCustomerAvailableServiceTypesAndAssociatedServices() {
    return this.graphApi.watchQuery<{ serviceTypes: ServiceType[], associatedServices: PlatformService[] }>({
      query: customerAvailableServiceTypesAndAssociatedServicesQuery,
      fetchPolicy: 'network-only',
    }).pipe(
      map(({ serviceTypes, associatedServices }) => ({
        serviceTypes: normalizeServiceTypes(serviceTypes),
        associatedServices: normalizePlatformServices(associatedServices),
      })),
    );
  }

  updatePlatformServiceName(serviceId: string, name: string) {
    return this.graphApi.mutate({
      mutation: updatePlatformServicNameMutation,
      variables: { input: { id: serviceId, name } },
    }).pipe(
      map(result => getMutationResult(result)),
    )
  }

  deletePlatformService(serviceId: string) {
    return this.graphApi.mutateWithCatch({
      mutation: deletePlatformServiceMutation,
      variables: { id: serviceId },
      msgPrefix: 'Failed to delete platform service',
    })
  }

  provisionPlatformService(serviceId: string) {
    return this.graphApi.mutateWithCatch({
      mutation: provisionPlatformServiceMutation,
      variables: { id: serviceId },
      msgPrefix: 'Failed to provision platform service',
    })
  }

  provisionPlatformServiceAfterAuthorization(serviceId: string) {
    return this.graphApi.mutateWithCatch({
      mutation: provisionPlatformServiceAfterAuthorizationMutation,
      variables: { id: serviceId },
      msgPrefix: 'Failed to provision platform service',
    })
  }

  deprovisionPlatformService(serviceId: string) {
    return this.graphApi.mutateWithCatch({
      mutation: deprovisionPlatformServiceMutation,
      variables: { id: serviceId },
      msgPrefix: 'Failed to deprovision platform service',
    })
  }

  enablePlatformService(serviceId: string) {
    return this.graphApi.mutateWithCatch({
      mutation: setPlatformServiceEnabledStatusMutation,
      variables: { id: serviceId, enabled: true },
      msgPrefix: 'Failed to enable platform service',
    })
  }

  disablePlatformService(serviceId: string) {
    return this.graphApi.mutateWithCatch({
      mutation: setPlatformServiceEnabledStatusMutation,
      variables: { id: serviceId, enabled: false },
      msgPrefix: 'Failed to disable platform service',
    })
  }

  getPlatformServiceReports(serviceId: string) {
    return this.graphApi.watchQuery<PlatformServiceReportSummary[]>({
      query: platformServiceReportsQuery,
      variables: { input: { serviceId } },
    });
  }

  getPlatforServiceEmbeddableReport(serviceId: string, reportId: number) {
    return this.graphApi.watchQuery<PlatformServiceEmbeddableReport>({
      query: platformServiceReportDetailsQuery,
      variables: { input: { serviceId, reportId } },
      fetchPolicy: 'network-only', // don't want to return the cached guest token
    });
  }

}
